import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { Menu, X, Phone, Calendar, Instagram, ChevronDown, MessageCircle, MapPin, Sun, Moon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import LanguageSwitcher from '../i18n/LanguageSwitcher';
import useDarkMode from '../hooks/useDarkMode';
import T from '../i18n/T';

// Countdown banner removed per owner — marketing interrupt, off-brand for radical simplicity.
// Component file deleted in wave 5b (along with HomepagePopup + ExitIntentPopup).
// TrustBar removed per owner request (was rendering as a broken/garbled bar
// on some devices due to font fallback on numeric glyphs). Trust signals
// remain elsewhere on the site (reviews section, footer, tour cards).
// ReservationsOnlyBanner import removed — owner asked to drop the top-of-page
// version. The file (src/components/ReservationsOnlyBanner.js) is kept on disk
// in case we want to reuse it inline somewhere later (footer, contact section).

const Header = () => {
  const { settings: siteSettings } = useSiteSettings();
  const [isDark, toggleTheme] = useDarkMode();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [events, setEvents] = useState([]);
  const [showEventsDropdown, setShowEventsDropdown] = useState(false);
  const [menuItems, setMenuItems] = useState(() => {
    try {
      const cached = localStorage.getItem('headerMenu');
      if (cached) return JSON.parse(cached);
    } catch (e) { /* ignore */ }
    return null;
  });
  const [socialLinks, setSocialLinks] = useState({
    facebook: 'https://www.facebook.com/nycPedicabrides',
    instagram: 'https://www.instagram.com/nycPedicabrides'
  });

  useEffect(() => {
    if (isMenuOpen) {
      // Lock scroll without position:fixed (which causes CLS in CrUX data)
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overscrollBehavior = 'none';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.overscrollBehavior = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.overscrollBehavior = '';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.events, { cache: 'no-store' });
        if (response.ok) {
          const data = await response.json();
          // API may return either a raw array or { events: [...] }
          const eventsList = Array.isArray(data) ? data : (data.events || []);
          // Only show active events in dropdown
          const activeEvents = eventsList.filter(e => e.is_active !== 0 && e.is_active !== false);
          setEvents(activeEvents);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };
    const idle = typeof requestIdleCallback === 'function'
      ? requestIdleCallback(() => fetchEvents())
      : setTimeout(fetchEvents, 2000);
    return () => typeof cancelIdleCallback === 'function' ? cancelIdleCallback(idle) : clearTimeout(idle);
  }, []);

  // Radically simplified nav — plain, one-word labels a first-time visitor
  // (any age, any language) reads without thinking. Quieter than the one Book CTA.
  // (Proposal, Christmas, Tour Guide, Blog moved out of the top nav for clarity.)
  const navItems = [
    { name: 'Tours', href: '/tours/' },
    { name: 'Our pedicabs', href: '/pedicabs/' },
    { name: 'Our horses', href: '/horses/' },
    { name: 'About', href: '/about/' },
    { name: 'Photos', href: '/gallery/' },
    { name: 'Questions', href: '/faq/' },
    { name: 'Contact', href: '/contact/' }
  ];

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        // Cache-bust to bypass Cloudflare edge cache for menu changes
        const response = await fetch('/api/menu', { cache: 'no-store' });
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            const items = data
              .filter(item => item.is_active === 1)
              .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
              .map(item => ({
                name: item.label,
                href: item.url,
                scrollId: '',
              }));
            if (items.length > 0) {
              // Always update state and cache with fresh data from API
              // (cached menu items show instantly; API updates replace them)
              setMenuItems(items);
              try { localStorage.setItem('headerMenu', JSON.stringify(items)); } catch (e) { /* ignore */ }
            }
          }
        }
      } catch (error) { /* fallback to navItems */ }
    };
    // Always fetch menu from API to catch admin changes (Christmas toggle etc.)
    // Cached localStorage menu is used as initial state for fast render,
    // then API response replaces it if different.
    const idle = typeof requestIdleCallback === 'function'
      ? requestIdleCallback(() => fetchMenu())
      : setTimeout(fetchMenu, 2500);
    return () => typeof cancelIdleCallback === 'function' ? cancelIdleCallback(idle) : clearTimeout(idle);
  }, []);

  // DB items override hardcoded navItems. We then:
  //   1. Force a 💍 prefix onto any Proposal entry (DB-managed menus may not
  //      carry the emoji; the owner wants it visible in every nav surface).
  //   2. Force-inject the Christmas Tour link if neither source includes it —
  //      this way the Christmas entry is guaranteed visible regardless of how
  //      the admin has configured the menu in the DB. Once Christmas ends
  //      (Jan 9, 2027) this injection can be removed.
  // Simplified: use the DB-managed menu if present, otherwise the lean navItems above.
  const displayMenuItems = menuItems || navItems;

  // Read social links from shared SiteSettingsContext (no extra fetch)
  useEffect(() => {
    if (siteSettings.social_facebook || siteSettings.social_instagram) {
      setSocialLinks({
        facebook: siteSettings.social_facebook || 'https://www.facebook.com/nycPedicabrides',
        instagram: siteSettings.social_instagram || 'https://www.instagram.com/nycPedicabrides'
      });
    }
  }, [siteSettings]);

  const location = useLocation();


  return (
    <>
    <a href="#main-content" className="skip-to-content"><T>Skip to content</T></a>
    <header
      style={{ animation: 'headerSlideDown 0.5s cubic-bezier(0.25, 1, 0.5, 1) both' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-sm' : 'bg-white'
      }`}
    >
      {/* ReservationsOnlyBanner removed from the top of every page — owner
          said it screamed too loudly above the fold. The same message lives
          inside the BookingModal step 1 (where it's actually relevant — the
          customer is mid-booking, not browsing) and can be re-surfaced in a
          more elegant inline form (Contact section / footer) on request. */}
      {/* Countdown banner removed — marketing interrupt, off-brand for radical simplicity. */}
      {/* TrustBar removed — owner reported it rendered as garbled ★ . . . ===+
          on certain device/font combos. Trust signals still surfaced via
          ReservationsOnlyBanner + footer + tour cards. */}
      {/* Top Bar — one quiet line: where to find us + a one-tap call */}
      <div className="hidden md:block py-2 border-b border-neutral-200/60 bg-white">
        <div className="container-custom">
          <div className="flex items-center justify-between text-xs text-neutral-500">
            <span className="flex items-center gap-1.5">
              <MapPin className="w-3 h-3 text-brand-500 flex-shrink-0" aria-hidden="true" />
              7th Ave &amp; Central Park South
            </span>
            <a href="tel:+16466569717" className="flex items-center gap-1.5 text-neutral-700 hover:text-brand-600 font-semibold transition-colors">
              <Phone className="w-3.5 h-3.5" aria-hidden="true" /><span>(646) 656-9717</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="container-custom">
        <div className="py-3 md:py-4">
          <div className="flex items-center justify-between md:mb-4">
            <Link to="/" aria-label="NY Central Park Tour home" className="inline-flex items-center transition-transform duration-200 hover:scale-[1.02]">
              <img
                src="/images/logo.png"
                alt="NY Central Park Tour"
                className="logo-light h-14 md:h-16 w-auto"
                width="620"
                height="696"
              />
              <img
                src="/images/logo-white.png"
                alt="NY Central Park Tour"
                className="logo-dark h-14 md:h-16 w-auto"
                width="620"
                height="696"
                aria-hidden="true"
              />
            </Link>

            <div className="flex items-center gap-2">
              {/* Mobile-only quick-call button — vivid emerald green so it
                  reads as a universal "call" affordance (the previous sage
                  was too muted; owner asked for clearly green). Matches the
                  bottom CTA bar's Call Us button for a consistent visual
                  language across all phone-call entry points. */}
              <a href="tel:+16466569717" aria-label="Call us" className="md:hidden bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white p-2.5 rounded-lg transition-colors shadow-sm">
                <Phone className="w-4 h-4" aria-hidden="true" strokeWidth={2.5} fill="currentColor" />
              </a>
              {/* Language switcher — persists choice in localStorage, drives the
                  useI18n() context throughout the site. */}
              <LanguageSwitcher compact />
              {/* Day / night theme toggle — persists in localStorage. */}
              <button
                onClick={toggleTheme}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                title={isDark ? 'Light mode' : 'Dark mode'}
                className="p-2 rounded-lg text-neutral-600 hover:text-brand-600 hover:bg-neutral-100 transition-colors"
              >
                {isDark
                  ? <Sun className="w-5 h-5" aria-hidden="true" />
                  : <Moon className="w-5 h-5" aria-hidden="true" />}
              </button>
              <div className="hidden lg:flex">
                <Link
                  to="/tours/"
                  className="bg-sunset-500 hover:bg-sunset-600 text-white px-6 py-2.5 rounded-lg font-bold text-sm transition-colors flex items-center gap-2 tracking-wide shadow-sm shadow-sunset-900/20"
                >
                  <Calendar className="w-4 h-4" aria-hidden="true" /><span><T>Book your ride</T></span>
                </Link>
              </div>
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label={isMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={isMenuOpen} className="xl:hidden p-2 text-neutral-700 hover:text-brand-600 transition-colors">
                {isMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
              </button>
            </div>
          </div>

          <nav className="hidden xl:block" aria-label="Main navigation">
            <div className="flex items-center gap-8">
              {displayMenuItems.map((item) => {
                // Highlight the Christmas Tour entry so it pops in the menu —
                // detect by the marker flag we set OR by href pattern (covers
                // both the hardcoded navItems and any DB-managed menu).
                const isChristmasItem = item.christmas === true || item.href === '/events/christmas/' || item.href === '/events/christmas';
                return (
                  <Link
                    key={item.name || item.id}
                    to={item.href}
                    onClick={(e) => {
                      if (location.pathname === item.href) {
                        const scrollId = item.scroll_id || item.scrollId;
                        if (scrollId) {
                          e.preventDefault();
                          const element = document.querySelector(`#${scrollId}`);
                          if (element) window.scrollTo({ top: element.offsetTop - 120, behavior: 'smooth' });
                        }
                      }
                    }}
                    className={
                      isChristmasItem
                        ? 'text-accent-700 hover:text-accent-800 font-semibold transition-colors text-sm relative group'
                        : 'text-neutral-600 hover:text-neutral-900 font-medium transition-colors text-sm relative group'
                    }
                  >
                    <T emoji>{item.name}</T>
                    <span className={
                      isChristmasItem
                        ? 'absolute -bottom-1 left-0 h-px bg-accent-700 transition-all duration-300 w-full'
                        : 'absolute -bottom-1 left-0 h-px bg-brand-500 transition-all duration-300 w-0 group-hover:w-full'
                    }></span>
                  </Link>
                );
              })}
              {(siteSettings?.events_nav_visible === true || siteSettings?.events_nav_visible === 'true') && events.length > 0 && (
                <div className="relative" onMouseEnter={() => setShowEventsDropdown(true)} onMouseLeave={() => setShowEventsDropdown(false)}>
                  <button className="text-neutral-600 hover:text-neutral-900 font-medium transition-colors text-sm relative group flex items-center gap-1">
                    <span>Events</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${showEventsDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  {showEventsDropdown && (
                    <div
                      className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-neutral-200 py-2 z-50"
                      style={{ animation: 'dropdownFadeIn 0.2s cubic-bezier(0.25, 1, 0.5, 1) both' }}
                    >
                      {events.map((event) => (
                        <a key={event.id} href={event.url || '#'} target={event.url ? '_blank' : '_self'} rel={event.url ? 'noopener noreferrer' : ''}
                          className="block px-4 py-3 text-sm text-neutral-700 hover:bg-brand-50 hover:text-brand-700 transition-colors border-b border-neutral-100 last:border-b-0">
                          <div className="font-medium">{event.name}</div>
                          {event.description && <div className="text-xs text-neutral-500 mt-1 line-clamp-2">{event.description}</div>}
                          {(event.start_date || event.end_date) && (
                            <div className="text-xs text-brand-600 mt-1">
                              {event.start_date && new Date(event.start_date).toLocaleDateString('en-US')}
                              {event.start_date && event.end_date && ' - '}
                              {event.end_date && new Date(event.end_date).toLocaleDateString('en-US')}
                            </div>
                          )}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </nav>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div
          className="xl:hidden bg-neutral-50/98 backdrop-blur-md border-t border-neutral-200 overflow-hidden"
          style={{ maxWidth: '100vw', animation: 'mobileMenuSlideDown 0.3s cubic-bezier(0.25, 1, 0.5, 1) both' }}
        >
          <div className="container-custom py-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 100px)' }}>
            <nav className="space-y-1">
              {displayMenuItems.map((item) => {
                const isChristmasItem = item.christmas === true || item.href === '/events/christmas/' || item.href === '/events/christmas';
                return (
                  <Link key={item.name || item.id} to={item.href} onClick={() => setIsMenuOpen(false)}
                    className={
                      isChristmasItem
                        ? 'block w-full text-left text-accent-700 hover:text-accent-800 hover:bg-accent-50 font-semibold py-3 px-3 rounded-lg transition-colors'
                        : 'block w-full text-left text-neutral-700 hover:text-neutral-900 hover:bg-brand-50 font-medium py-3 px-3 rounded-lg transition-colors'
                    }>
                    <T emoji>{item.name}</T>
                  </Link>
                );
              })}
              {(siteSettings?.events_nav_visible === true || siteSettings?.events_nav_visible === 'true') && events.length > 0 && (
                <div className="pt-3 mt-3 border-t border-neutral-200">
                  <div className="text-xs font-semibold text-neutral-400 mb-2 px-3 uppercase tracking-wider">Events</div>
                  {events.map((event) => (
                    <a key={event.id} href={event.url || '#'} target={event.url ? '_blank' : '_self'} rel={event.url ? 'noopener noreferrer' : ''}
                      className="block text-neutral-600 hover:text-brand-700 py-2.5 px-3 rounded-lg hover:bg-brand-50 transition-colors">
                      <div className="font-medium text-sm">{event.name}</div>
                    </a>
                  ))}
                </div>
              )}
              <div className="pt-6 mt-3 border-t border-neutral-200 space-y-4">
                <Link to="/tours/" onClick={() => setIsMenuOpen(false)}
                  className="bg-sunset-500 hover:bg-sunset-600 text-white px-6 py-3 rounded-lg font-bold w-full flex items-center justify-center gap-2 transition-colors">
                  <Calendar className="w-4 h-4" aria-hidden="true" /><span><T>Book your ride</T></span>
                </Link>
                <a href="tel:+16466569717" className="flex items-center gap-2 text-neutral-700 hover:text-brand-600 font-medium px-3 transition-colors">
                  <Phone className="w-4 h-4" aria-hidden="true" /><span>(646) 656-9717</span>
                </a>
                <div className="flex flex-wrap items-center gap-5 px-3">
                  <a href="https://wa.me/16466569717" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-neutral-600 hover:text-brand-600 text-sm transition-colors">
                    <MessageCircle className="w-4 h-4" aria-hidden="true" /><span>WhatsApp</span>
                  </a>
                  <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-neutral-600 hover:text-brand-600 text-sm transition-colors">
                    <Instagram className="w-4 h-4" aria-hidden="true" /><span>Instagram</span>
                  </a>
                </div>
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
    </>
  );
};

export default Header;
